import {makeAutoObservable, runInAction} from "mobx";
import { rootContainer, TYPE } from "../../../../services/container/root";
import AdminMigrateGlobalEMerchantsService, { GlobalEEnvironment } from "../../../../services/api/admin-migrate-global-e-merchants-service";
import UserService, {IUserIdentity} from "../../../../services/api/user-service";

export enum MerchantIdsValidationError {
    TOO_MANY = "too_many",
    INVALID_FORMAT = "invalid_format",
}

export interface IMigrateGlobalEMerchantsRequestData {
    merchantIds: string[];
    environment?: GlobalEEnvironment;
}

class MigrateGlobalEMerchantsPageStore {
    private _userIdentity: IUserIdentity | null = null;

    private _submittedRequest: IMigrateGlobalEMerchantsRequestData | null = null;
    private _isLoadingInitialData = false;
    private _isSubmittingMigrationRequest = false;

    private _startMigrationRequestError: string | null = null;

    private _rawMerchantIds = '';

    private _request!: IMigrateGlobalEMerchantsRequestData;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
        this.emptyRequest();
    }

    public async fetchInitialData(): Promise<void> {
        this._isLoadingInitialData = true;

        await this.loadAdminUserIdentity();

        this._isLoadingInitialData = false;
    }

    public getAdminUserEmail(): string {
        return this._userIdentity?.logged_in_user_email ?? '';
    }

    public getSubmittedRequest(): IMigrateGlobalEMerchantsRequestData | null {
        return this._submittedRequest;
    }

    public isLoadingInitialData(): boolean {
        return this._isLoadingInitialData;
    }

    public getMerchantIdsRaw(): string {
        return this._rawMerchantIds;
    }

    public setMerchantIds(value: string): void {
        this._rawMerchantIds = value;

        const trimmed = value.trim();
        if (!trimmed) {
            this._request.merchantIds = [];
            return;
        }
        this._request.merchantIds = trimmed
            .split(";")
            .map(t => t.trim())
            .filter(Boolean);
    }

    public getEnvironment(): GlobalEEnvironment | undefined {
        return this._request.environment;
    }

    public setEnvironment(value: GlobalEEnvironment): void {
        this._request.environment = value;
    }

    public getMerchantIds(): string[] {
        return this._request.merchantIds;
    }

    public getParsedMerchantIds(): string[] {
        return this.getMerchantIds();
    }

    public getMerchantIdsCount(): number {
        return this.getMerchantIds().length;
    }

    public getMerchantIdsValidationError(): MerchantIdsValidationError | null {
        const parsedIds = this.getMerchantIds();

        if (parsedIds.length > 100) {
            return MerchantIdsValidationError.TOO_MANY;
        }

        // check if all ids are numbers
        const isFormatValid = parsedIds.every(t => /^\d+$/.test(t.toString()));
        if (!isFormatValid) {
            return MerchantIdsValidationError.INVALID_FORMAT;
        }

        return null;
    }

    public getStartMigrationRequestError(): string | null {
        return this._startMigrationRequestError;
    }

    public isSubmittingMigrationRequest(): boolean {
        return this._isSubmittingMigrationRequest;
    }

    public async submitMigrationRequest(): Promise<void> {
        if (!this._request.environment) {
            return;
        }

        this._startMigrationRequestError = null; // clear previous errors
        this._isSubmittingMigrationRequest = true;
        this._submittedRequest = null;

        try {
            await this._adminMigrateGlobalEMerchantsService.migrateGlobalEMerchants({
                merchantIds: this._request.merchantIds,
                environment: this._request.environment,
            });
            this._startMigrationRequestError = null;
            this._submittedRequest = { ...this._request };
            this.emptyRequest();
        } catch (e: any) {
            this._startMigrationRequestError = e?.message || "Unknown error during API call";
        } finally {
            this._isSubmittingMigrationRequest = false;
        }
    }

    private async loadAdminUserIdentity(): Promise<void> {
        try {
            const userService = rootContainer.get<UserService>(TYPE.UserService);
            const userIdentity = await userService.getCurrentPortalIdentity();

            runInAction(() => {
                this._userIdentity = userIdentity;
            });
        } catch (e) {
            console.error('[MigrateGlobalEMerchantsPageStore::loadAdminUserIdentity] failed:', e);
        }
    }

    private get _adminMigrateGlobalEMerchantsService(): AdminMigrateGlobalEMerchantsService {
        return rootContainer.get<AdminMigrateGlobalEMerchantsService>(TYPE.AdminMigrateGlobalEMerchantsService);
    }

    private emptyRequest(): void {
        this._rawMerchantIds = '';
        this._request = {
            merchantIds: [],
        };
    }
}

export default new MigrateGlobalEMerchantsPageStore();
